import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import s3Service from "../services/s3Service";
import {
  validateProperty,
  getFieldErrorClass,
  getFieldErrorMessage,
} from "../utils/propertyValidation";
import Loader from "../components/Loader";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { parse, format, isValid } from "date-fns";

// Helper functions for UK date format (DD/MM/YYYY) - matching Laravel
const parseDate = (dateString) => {
  if (!dateString) return null;
  // Handle YYYY-MM-DD format from backend
  if (/^\d{4}-\d{2}-\d{2}/.test(dateString)) {
    const date = new Date(dateString);
    return isValid(date) ? date : null;
  }
  // Handle DD/MM/YYYY format
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
    const parsed = parse(dateString, "dd/MM/yyyy", new Date());
    return isValid(parsed) ? parsed : null;
  }
  return null;
};

const formatDateForBackend = (date) => {
  if (!date || !isValid(date)) return "";
  return format(date, "yyyy-MM-dd");
};

// Enhanced error class with inline styles for visual feedback - match user form style
const getFieldErrorStyle = (validationErrors, fieldName, hasSubmitted) => {
  return validationErrors[fieldName] && hasSubmitted
    ? {
        borderColor: "#dc3545",
        borderWidth: "1px",
        borderStyle: "solid",
      }
    : {};
};

// If you have a shared rich text editor component, import it here
// import RichTextEditor from '../components/RichTextEditor';

// Liability types are now loaded from backend via formData.liabilityTypes

const staticDropdown = (options) =>
  options.map((opt) => (
    <option key={opt} value={opt}>
      {opt}
    </option>
  ));

const requiredAsteriskStyle = { color: "red", marginLeft: "2px" };

// Error message styling
const errorMessageStyle = {
  color: "#dc3545",
  fontSize: "0.875rem",
  marginTop: "0.25rem",
  display: "block",
  fontWeight: "bold",
  backgroundColor: "#f8d7da",
  padding: "4px 8px",
  borderRadius: "4px",
  border: "1px solid #f5c6cb",
};

// Error message styling - match the user form style
const getErrorMessageStyle = (hasSubmitted) => ({
  color: "#dc3545",
  fontSize: "0.875rem",
  marginTop: "0.25rem",
  display: hasSubmitted ? "block" : "none",
  fontWeight: "bold",
  backgroundColor: "#f8d7da",
  padding: "4px 8px",
  borderRadius: "4px",
  border: "1px solid #f5c6cb",
});

// Helper function to fetch available property ID from backend (matches Laravel getAvailablePropertyID)
const fetchAvailablePropertyId = async () => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/properties/generate-id`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );
    const result = await response.json();
    if (result.success && result.data?.propertyId) {
      return result.data.propertyId;
    }
    throw new Error("Failed to generate property ID");
  } catch (error) {
    console.error("Error fetching property ID:", error);
    // Fallback: generate a temporary ID (will be overwritten by backend on form load)
    return "";
  }
};

const PropertyCreate = () => {
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('client_id');
  const navigate = useNavigate();

  console.log('🎯 [PropertyCreate] Component rendered with clientId:', clientId);

  // Form data state
  const [formData, setFormData] = useState({
    clients: {},
    clientTypes: {},
    clientId: clientId || null,
    locations: {},
    types: {},
    buildingManagers: {},
    manualStatuses: {},
    liabilityTypes: [],
    liabilityOptions: {},
    availablePropertyId: "",
  });

  // File upload state
  const [mainPhoto, setMainPhoto] = useState(null);
  const [mainPhotoKey, setMainPhotoKey] = useState("");
  const [mainPhotoUploading, setMainPhotoUploading] = useState(false);
  const [mainPhotoProgress, setMainPhotoProgress] = useState(0);

  // Form state
  const [form, setForm] = useState({
    name: "",
    property_id: "", // Will be populated from backend API
    status: "",
    status_manual: "",
    residential_commercial: "",
    client_id: clientId || "",
    client_contact_id: "",
    building_manager_id: "",
    block_id: "",
    inspection_route_id: "",
    type_id: "",
    location_id: "",
    address_1: "",
    address_2: "",
    address_3: "",
    town: "",
    postcode: "",
    country: "",
    county: "",
    geo_latitude: "",
    geo_longitude: "",
    price_from: "",
    price_to: "",
    actual_market_rent: "",
    square_footage: "",
    rateable_value: "",
    rebuild_value: "",
    spaces: "",
    bathrooms: "",
    kitchens: "",
    garden: "",
    parking: "",
    vip_rule_type: "",
    vip_amount: "",
    vip_provided: "",
    vip_amount_rule: "",
    vip_date_to_pay: "",
    total_vip_paid: "",
    savings_made: "",
    date_taken_on: "",
    date_taken_back: "",
    date_live: "",
    date_notice: "",
    date_feature_from: "",
    date_feature_to: "",
    short_description: "",
    description: "",
    fit_out_notes: "",
    utilities_notes: "",
    public_description: "",
    public_short_description: "",
    website_display_name: "",
    video: "",
    main_photo: null,
    photos: [],
    monthly_inspection_status: "",
    client_type: "",
    liability: {},
  });

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [clientContacts, setClientContacts] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [vipRules, setVipRules] = useState([]); // Dynamic VIP rules for unique VIP rule type

  // Fetch form data on component mount
  useEffect(() => {
    fetchFormData();
  }, [clientId]);

  // Fetch client contacts when component mounts if clientId is provided
  useEffect(() => {
    if (clientId) {
      fetchClientContacts(clientId);
    }
  }, [clientId]);

  // Clear invalid client_contact_id when clientContacts changes
  useEffect(() => {
    if (Object.keys(clientContacts).length > 0) {
      setForm((prev) => {
        const currentContactId = prev.client_contact_id;
        // If currentContactId is not empty and is not a valid number, or not in contacts, clear it
        if (currentContactId && (isNaN(currentContactId) || !clientContacts.hasOwnProperty(currentContactId))) {
          console.log(`⚠️ [PropertyCreate] Clearing invalid client_contact_id: ${currentContactId}`);
          return { ...prev, client_contact_id: "" };
        }
        return prev;
      });
    }
  }, [clientContacts]);

  // Fetch form data from backend
  const fetchFormData = async () => {
    try {
      setLoading(true);
      setError(""); // Clear any previous errors

      const url = clientId
        ? `${process.env.REACT_APP_API_URL}/api/properties/create/${clientId}`
        : `${process.env.REACT_APP_API_URL}/api/properties/create`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to fetch form data: ${response.status} ${response.statusText}`
        );
      }

      const result = await response.json();

      if (result.success) {
        setFormData(result.data);
        // Set the available property ID as default
        setForm((prev) => ({
          ...prev,
          property_id: result.data.availablePropertyId || "",
        }));
      } else {
        throw new Error(result.message || "Failed to fetch form data");
      }
    } catch (error) {
      console.error("Error fetching form data:", error);
      setError(
        `Failed to load form data: ${error.message}. Please refresh the page.`
      );
    } finally {
      setLoading(false);
    }
  };

  // Fetch client contacts when client changes
  const fetchClientContacts = async (clientId) => {
    if (!clientId) {
      console.log("❌ [PropertyCreate] No clientId provided to fetchClientContacts");
      setClientContacts({});
      return;
    }

    try {
      console.log(`🔍 [PropertyCreate] Fetching client contacts for clientId: ${clientId}`);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/client-contacts?client_id=${clientId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        console.log("✅ [PropertyCreate] Client contacts API response:", result);

        // Check if result has data property (API response structure: { "success": true, "data": [...] })
        const contactsArray = result.data || result;

        const contacts = { "": "--- Select contact ---" };
        contactsArray.forEach((contact) => {
          // Use the display_name from the API, or fallback to the old logic
          const displayName =
            contact.display_name ||
            (contact.first_name && contact.last_name
              ? `${contact.first_name} ${contact.last_name}`.trim()
              : null) ||
            contact.first_name ||
            contact.last_name ||
            contact.name ||
            "Unknown Contact";
          contacts[contact.id] = displayName;
        });
        console.log(`✅ [PropertyCreate] Loaded ${Object.keys(contacts).length - 1} client contacts`);
        setClientContacts(contacts);
      } else {
        console.error(`❌ [PropertyCreate] Failed to fetch client contacts: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error("❌ [PropertyCreate] Error fetching client contacts:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      setForm({
        ...form,
        [name]: files.length > 1 ? Array.from(files) : files[0],
      });
    } else {
      // For client_contact_id, ensure only numeric values or empty string are accepted
      let processedValue = value;
      if (name === "client_contact_id") {
        // Only allow empty string or numeric values
        if (value === "" || (!isNaN(value) && value !== "")) {
          processedValue = value;
        } else {
          // If invalid value, clear it
          console.log(`⚠️ [PropertyCreate] Invalid client_contact_id value: ${value}, clearing it`);
          processedValue = "";
        }
      }
      
      setForm({ ...form, [name]: processedValue });

      // If client_id changes, fetch client contacts
      if (name === "client_id") {
        fetchClientContacts(value);
        // Clear client contact when client changes
        setForm((prev) => ({ ...prev, client_contact_id: "" }));
      }

      // If VIP rule type changes to unique (2), ensure vipRules array exists
      if (name === "vip_rule_type" && value === "2" && vipRules.length === 0) {
        setVipRules([{ months: "Onwards", amount: "" }]);
      }

      // Clear validation error for this field when user starts typing
      if (validationErrors[name]) {
        setValidationErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
      }
    }
  };

  // VIP Rules handlers
  const handleAddVipRule = () => {
    setVipRules([...vipRules, { months: "Onwards", amount: "" }]);
  };

  const handleRemoveVipRule = (index) => {
    const updatedRules = vipRules.filter((_, i) => i !== index);
    setVipRules(updatedRules);

    // Clear the VIP amount validation error once all remaining rules have an amount
    if (
      validationErrors.vip_amount_rule &&
      updatedRules.length > 0 &&
      updatedRules.every((rule) => rule.amount && rule.amount.trim() !== "")
    ) {
      setValidationErrors((prev) => {
        const { vip_amount_rule, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleVipRuleChange = (index, field, value) => {
    const updatedRules = [...vipRules];
    updatedRules[index] = { ...updatedRules[index], [field]: value };
    setVipRules(updatedRules);

    // Clear the VIP amount validation error once all rules have an amount
    if (
      validationErrors.vip_amount_rule &&
      updatedRules.length > 0 &&
      updatedRules.every((rule) => rule.amount && rule.amount.trim() !== "")
    ) {
      setValidationErrors((prev) => {
        const { vip_amount_rule, ...rest } = prev;
        return rest;
      });
    }
  };

  // Radios can't normally be unchecked; clicking the already-selected VIP rule
  // clears the selection (field is optional).
  const handleVipRuleTypeClick = (value) => {
    if (String(form.vip_rule_type) === String(value)) {
      setForm((prev) => ({ ...prev, vip_rule_type: "" }));
      if (validationErrors.vip_amount_rule) {
        setValidationErrors((prev) => {
          const { vip_amount_rule, ...rest } = prev;
          return rest;
        });
      }
    }
  };

  // Handle field blur - validate individual field when user leaves it
  const handleBlur = (e) => {
    const { name, value } = e.target;

    // Always validate on blur to show immediate feedback
    const fieldValidation = validateProperty({ [name]: value }, false);
    if (fieldValidation.errors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: fieldValidation.errors[name],
      }));
    } else {
      // Clear error for this field if it's now valid
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Manual validation trigger for debugging
  const triggerValidation = () => {
    const validation = validateProperty(form, false);
    setValidationErrors(validation.errors);
    setHasSubmitted(true); // Set hasSubmitted to true when manually triggering validation
  };

  // File upload handlers
  const handleMainPhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
    ];
    if (!allowedTypes.includes(file.type)) {
      setError("Please select a valid image file (JPEG, PNG, GIF, or WebP)");
      return;
    }

    // Validate file size (100MB max)
    const maxSize = 100 * 1024 * 1024; // 100MB
    if (file.size > maxSize) {
      setError("File size must be less than 100MB");
      return;
    }

    try {
      setMainPhotoUploading(true);
      setMainPhotoProgress(0);
      setError("");

      // Upload to S3 - Use Laravel-compatible path for main photos
      const s3Key = await s3Service.uploadImage(file, {
        maxSize: 100 * 1024 * 1024, // 100MB
        allowedTypes: allowedTypes,
        folder: "uploads/images/properties/main_photos", // Laravel: config('property.main_photo_path')
        onProgress: (progress) => {
          setMainPhotoProgress(progress);
        },
      });

      setMainPhoto(file);
      setMainPhotoKey(s3Key);
      setForm((prev) => ({ ...prev, main_photo: s3Key }));

      console.log("Main photo uploaded successfully:", s3Key);
    } catch (error) {
      console.error("Error uploading main photo:", error);
      setError(`Failed to upload image: ${error.message}`);
    } finally {
      setMainPhotoUploading(false);
      setMainPhotoProgress(0);
    }
  };

  const removeMainPhoto = () => {
    setMainPhoto(null);
    setMainPhotoKey("");
    setForm((prev) => ({ ...prev, main_photo: null }));
  };

  // Handle geocoding from postcode - Use Laravel backend endpoint (postcodes.io)
  const handleGenerateGeoCodes = async () => {
    if (!form.postcode) {
      setError("Please enter a postcode first");
      return;
    }

    try {
      setIsGeocoding(true);
      setError("");

      // Use Laravel backend endpoint (postcodes.io API) - matches Laravel implementation
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/external/generate-geo-codes-from-postcode`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ postcode: form.postcode }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to generate geo codes: ${response.statusText}`);
      }

      const data = await response.json();

      // Laravel response format: { status: 200, result: { latitude, longitude } } or { status: 'fallback', result: null, message: '...' }
      if (data.status === 200 && data.result && data.result.latitude && data.result.longitude) {
        // Update form with latitude and longitude
        setForm((prev) => ({
          ...prev,
          geo_latitude: data.result.latitude.toString(),
          geo_longitude: data.result.longitude.toString(),
        }));

        console.log("Geocoding successful:", data.result);
      } else if (data.status === 'fallback') {
        // Laravel fallback response - postcode not found or service unavailable
        const errorMessage = data.message || "Could not find coordinates for this postcode. Please check the postcode and try again.";
        setError(errorMessage);
        console.warn("Geocoding failed:", data);
      } else {
        // Unexpected response format
        setError(
          "Could not find coordinates for this postcode. Please check the postcode and try again."
        );
      }
    } catch (err) {
      console.error("Error geocoding postcode:", err);
      setError("Failed to generate geo codes. Please try again.");
    } finally {
      setIsGeocoding(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("🔵 [PROPERTY CREATE] Form submit triggered");
    setError("");
    setValidationErrors({});
    setHasSubmitted(true);
    setIsSubmitting(true);

    try {
      // Validate form data
      console.log("🔵 [PROPERTY CREATE] Validating form data...");
      const validation = validateProperty(form, false);
      console.log("🔵 [PROPERTY CREATE] Validation result:", validation);
      
      // Additional validation for VIP rules when type is unique (2)
      if (form.vip_rule_type === "2" || form.vip_rule_type === 2) {
        if (vipRules.length === 0) {
          validation.errors.vip_amount_rule = "At least one VIP rule is required when VIP Rule Type is Unique";
          validation.isValid = false;
        } else {
          const hasInvalidRule = vipRules.some(rule => !rule.amount || rule.amount.trim() === "");
          if (hasInvalidRule) {
            validation.errors.vip_amount_rule = "All VIP rules must have an amount";
            validation.isValid = false;
          }
        }
      }
      
      if (!validation.isValid) {
        console.log("❌ [PROPERTY CREATE] Validation failed:", validation.errors);
        setValidationErrors(validation.errors);
        setIsSubmitting(false);
        return;
      }
      console.log("✅ [PROPERTY CREATE] Validation passed, proceeding with submission...");

      // Prepare form data for submission
      const submitData = { ...form };

      // Convert empty strings to null for optional fields
      // EXCEPT for address and location fields - keep as empty strings
      // Laravel allows these to be empty strings, and database may have NOT NULL constraint
      const stringFields = ["address_1", "address_2", "address_3", "town", "postcode"];
      Object.keys(submitData).forEach((key) => {
        if (submitData[key] === "" && !stringFields.includes(key)) {
          submitData[key] = null;
        }
        // For address and location fields, ensure they're empty strings (not null or undefined)
        if (stringFields.includes(key) && (submitData[key] === null || submitData[key] === undefined)) {
          submitData[key] = "";
        }
      });

      // Convert dates from YYYY-MM-DD to DD/MM/YYYY format for backend
      const convertDateForBackend = (dateString) => {
        if (!dateString || dateString === "") return null;

        // If already in DD/MM/YYYY format, return as is
        if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString)) {
          return dateString;
        }

        // If in YYYY-MM-DD format (from date input), convert to DD/MM/YYYY
        if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(dateString)) {
          const [year, month, day] = dateString.split("-");
          return `${day.padStart(2, "0")}/${month.padStart(2, "0")}/${year}`;
        }

        return dateString; // Return original if can't parse
      };

      // Convert all date fields
      const dateFields = [
        "date_taken_on",
        "date_live",
        "date_taken_back",
        "date_feature_from",
        "date_feature_to",
      ];
      dateFields.forEach((field) => {
        if (submitData[field]) {
          submitData[field] = convertDateForBackend(submitData[field]);
        }
      });

      // Handle VIP rule type - convert empty string to null, or convert to integer
      if (submitData.vip_rule_type === "" || submitData.vip_rule_type === null || submitData.vip_rule_type === undefined) {
        submitData.vip_rule_type = null;
      } else {
        // Convert to integer if it's a valid value
        const vipRuleTypeInt = parseInt(submitData.vip_rule_type, 10);
        if (!isNaN(vipRuleTypeInt)) {
          submitData.vip_rule_type = vipRuleTypeInt;
        } else {
          submitData.vip_rule_type = null;
        }
      }

      // Handle integer fields - convert empty strings to null, or convert to integer
      const integerFields = [
        "client_type",
        "client_contact_id",
        "building_manager_id",
        "location_id",
        "type_id",
        "block_id",
        "inspection_route_id"
      ];

      integerFields.forEach((field) => {
        if (submitData[field] === "" || submitData[field] === null || submitData[field] === undefined) {
          submitData[field] = null;
        } else {
          // Convert to integer if it's a valid value
          const intValue = parseInt(submitData[field], 10);
          if (!isNaN(intValue)) {
            submitData[field] = intValue;
          } else {
            submitData[field] = null;
          }
        }
      });

      // Serialize VIP rules to JSON if VIP rule type is unique (2)
      if (submitData.vip_rule_type === 2) {
        if (vipRules.length > 0) {
          // Filter out empty rules and serialize to JSON
          const validRules = vipRules.filter(rule => rule.amount && rule.amount.trim() !== "");
          submitData.vip_amount_rule = JSON.stringify(validRules);
        } else {
          submitData.vip_amount_rule = null;
        }
      } else {
        // For non-unique VIP rules, set vip_amount_rule to null
        submitData.vip_amount_rule = null;
      }

      // Submit data to API
      console.log("🔵 [PROPERTY CREATE] Submitting data to API...");
      console.log("🔵 [PROPERTY CREATE] Submit data:", JSON.stringify(submitData, null, 2).substring(0, 500));

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/properties`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify(submitData),
        }
      );

      console.log("🔵 [PROPERTY CREATE] API response status:", response.status);
      const result = await response.json();
      console.log("🔵 [PROPERTY CREATE] API response:", result);

      if (response.ok) {
        console.log("✅ [PROPERTY CREATE] Property created successfully, navigating...");
        navigate("/properties");
      } else {
        console.log("❌ [PROPERTY CREATE] API error:", result);
        // Handle server-side validation errors
        if (result.errors) {
          setValidationErrors(result.errors);
        } else if (result.message) {
          // Check if it's a duplicate property ID error
          if (
            result.message.includes("property ID") &&
            result.message.includes("already exists")
          ) {
            setValidationErrors({
              property_id: result.message,
            });
          } else {
            setError(result.message || "Failed to create property");
          }
        } else {
          setError("Failed to create property");
        }
      }
    } catch (err) {
      console.error("Error creating property:", err);

      // Check if it's a network error with response data
      if (err.response && err.response.data) {
        const errorData = err.response.data;
        if (errorData.errors) {
          setValidationErrors(errorData.errors);
        } else if (errorData.message) {
          // Check if it's a duplicate property ID error
          if (
            errorData.message.includes("property ID") &&
            errorData.message.includes("already exists")
          ) {
            setValidationErrors({
              property_id: errorData.message,
            });
          } else {
            setError(errorData.message);
          }
        } else {
          setError("Failed to create property");
        }
      } else {
        setError(err.message || "Failed to create property");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // if (loading) {
  //   return (
  //     <div className="container" style={{ maxWidth: 1100, margin: "0 auto" }}>
  //       <div className="breadcrumb-row">
  //         <span className="breadcrumb-main">Dashboard</span>
  //         <span className="breadcrumb-sep">/</span>
  //         <span className="breadcrumb-properties">Properties</span>
  //         <span className="breadcrumb-sep">/</span>
  //         <span className="breadcrumb-create">Add Property</span>
  //       </div>
  //       <div
  //         className="panel panel-default panel-form"
  //         style={{ margin: "0 auto", marginTop: 24 }}
  //       >
  //         <div className="panel-body text-center">
  //           <div className="spinner-border" role="status">
  //             <span className="sr-only">Loading...</span>
  //           </div>
  //           <p>Loading form data...</p>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="container" style={{ maxWidth: 1100, margin: "0 auto" }}>
      <div className="breadcrumb-row">
        <span
          className="breadcrumb-main"
          onClick={() => navigate("/dashboard")}
          style={{ cursor: "pointer", color: "#337ab7" }}
        >
          Dashboard
        </span>
        <span className="breadcrumb-sep">/</span>
        <span
          className="breadcrumb-properties"
          onClick={() => navigate("/properties")}
          style={{ cursor: "pointer", color: "#337ab7" }}
        >
          Properties
        </span>
        <span className="breadcrumb-sep">/</span>
        <span className="breadcrumb-create">Add Property</span>
      </div>
      <form
        className="panel panel-default panel-form"
        onSubmit={handleSubmit}
        style={{ margin: "0 auto", marginTop: 24 }}
      >
        <div className="panel-heading panel-title">
          <h3>Add Property</h3>
        </div>
        <div
          className="panel-body"
          style={{ display: "flex", flexDirection: "column", gap: 32 }}
        >
          {/* General Section */}
          <div className="section mb-4">
            <div
              className="panel-body"
              // style={{ display: "flex", flexWrap: "wrap", gap: 24 }}
            >
              <div className="form-group create-property-input-field">
                <label>
                  Name <span>*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  className={getFieldErrorClass(validationErrors, "name")}
                  style={getFieldErrorStyle(
                    validationErrors,
                    "name",
                    hasSubmitted
                  )}
                  value={form.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                />
                {validationErrors.name && (
                  <div style={getErrorMessageStyle(hasSubmitted)}>
                    {validationErrors.name}
                  </div>
                )}
              </div>
              <div className="form-group create-property-input-field-id">
                <label>
                  Property ID <span>*</span>
                </label>
                <div
                  style={{ display: "flex", gap: "10px", alignItems: "center" }}
                >
                  <input
                    type="text"
                    name="property_id"
                    className={getFieldErrorClass(
                      validationErrors,
                      "property_id"
                    )}
                    style={{
                      ...getFieldErrorStyle(
                        validationErrors,
                        "property_id",
                        hasSubmitted
                      ),
                      flex: 1,
                    }}
                    value={form.property_id}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                  />
                  <button
                    type="button"
                    onClick={async () => {
                      const newId = await fetchAvailablePropertyId();
                      if (newId) {
                        setForm((prev) => ({ ...prev, property_id: newId }));
                        // Clear any property_id validation error
                        setValidationErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.property_id;
                          return newErrors;
                        });
                      }
                    }}
                    style={{
                      padding: "8px 12px",
                      backgroundColor: "#5bc0de",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "12px",
                      whiteSpace: "nowrap",
                    }}
                    title="Generate new unique Property ID"
                  >
                    Generate New ID
                  </button>
                </div>
                {validationErrors.property_id && (
                  <div style={getErrorMessageStyle(hasSubmitted)}>
                    {validationErrors.property_id}
                  </div>
                )}
                <p>System suggests the lowest available number.</p>
              </div>
              <div className="form-group create-property-input-field-Status">
                <label>
                  Status <span>*</span>
                </label>
                <select
                  name="status_manual"
                  className={getFieldErrorClass(
                    validationErrors,
                    "status_manual"
                  )}
                  style={getFieldErrorStyle(
                    validationErrors,
                    "status_manual",
                    hasSubmitted
                  )}
                  value={form.status_manual}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                >
                  <option value="">--- Select Status ---</option>
                  {Object.entries(formData.manualStatuses).map(
                    ([key, value]) => (
                      <option key={key} value={key}>
                        {value}
                      </option>
                    )
                  )}
                </select>
                {validationErrors.status_manual && (
                  <div style={getErrorMessageStyle(hasSubmitted)}>
                    {validationErrors.status_manual}
                  </div>
                )}
              </div>
              <div
                className="form-group create-property-input-field-text"
                style={{ width: "444px", margin: "auto" }}
              >
                <p>
                  When <span>Automatic</span> the system will try to set: Under
                  Offer, Empty, Not Full, Full, Handing Back or Ended
                </p>
              </div>
              {/* Residential/Commercial - required, between Status and Client
                  as the client's Sept 2026 layout asks. Drives the Building Set
                  Up KPI deadline: 14 calendar days residential, 28 commercial. */}
              <div className="form-group create-property-input-field-Status">
                <label htmlFor="residential_commercial">
                  Residential/Commercial <span>*</span>
                </label>
                <select
                  id="residential_commercial"
                  name="residential_commercial"
                  className={getFieldErrorClass(
                    validationErrors,
                    "residential_commercial"
                  )}
                  style={getFieldErrorStyle(
                    validationErrors,
                    "residential_commercial",
                    hasSubmitted
                  )}
                  value={form.residential_commercial}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                >
                  <option value="">--- Select ---</option>
                  <option value="residential">Residential</option>
                  <option value="commercial">Commercial</option>
                </select>
                {validationErrors.residential_commercial && (
                  <div style={getErrorMessageStyle(hasSubmitted)}>
                    {validationErrors.residential_commercial}
                  </div>
                )}
              </div>
              <div className="form-group create-property-input-field-Status">
                <label>
                  Client <span>*</span>
                </label>
                <select
                  name="client_id"
                  className={getFieldErrorClass(validationErrors, "client_id")}
                  style={getFieldErrorStyle(
                    validationErrors,
                    "client_id",
                    hasSubmitted
                  )}
                  value={form.client_id}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                >
                  {loading ? (
                    <option value="">Loading clients...</option>
                  ) : Object.keys(formData.clients).length === 0 ? (
                    <option value="">No clients available</option>
                  ) : (
                    Object.entries(formData.clients).map(([key, value]) => (
                      <option key={key} value={key}>
                        {value}
                      </option>
                    ))
                  )}
                </select>
                {validationErrors.client_id && (
                  <div style={getErrorMessageStyle(hasSubmitted)}>
                    {validationErrors.client_id}
                  </div>
                )}
              </div>
              <div className="form-group create-property-input-field-Status">
                <label>Client Type</label>
                <select
                  type="text"
                  name="client_type"
                  className="form-control"
                  value={form.client_type}
                  onChange={handleChange}
                >
                  <option value="" disabled hidden>
                    Select client type
                  </option>
                  {loading ? (
                    <option value="">Loading client types...</option>
                  ) : Object.keys(formData.clientTypes).length === 0 ? (
                    <option value="">No client types available</option>
                  ) : (
                    Object.entries(formData.clientTypes).map(([key, value]) => (
                      <option key={key} value={key}>
                        {value}
                      </option>
                    ))
                  )}
                </select>
              </div>
              <div className="form-group create-property-input-field-Status">
                <label>Client Contact</label>
                <select
                  name="client_contact_id"
                  className={getFieldErrorClass(
                    validationErrors,
                    "client_contact_id"
                  )}
                  value={form.client_contact_id}
                  onChange={handleChange}
                  disabled={!form.client_id}
                >
                  {Object.entries(clientContacts).map(([key, value]) => (
                    <option key={key} value={key}>
                      {value}
                    </option>
                  ))}
                </select>
                {validationErrors.client_contact_id && (
                  <div style={getErrorMessageStyle(hasSubmitted)}>
                    {validationErrors.client_contact_id}
                  </div>
                )}
              </div>
              <div className="form-group create-property-input-field-Status">
                <label>Building Manager</label>
                <select
                  name="building_manager_id"
                  className="form-control"
                  value={form.building_manager_id}
                  onChange={handleChange}
                >
                  {loading ? (
                    <option value="">Loading building managers...</option>
                  ) : Object.keys(formData.buildingManagers).length <= 1 ? (
                    <option value="">
                      No building managers available
                    </option>
                  ) : (
                    Object.entries(formData.buildingManagers).map(
                      ([key, value]) => (
                        <option key={key} value={key}>
                          {value}
                        </option>
                      )
                    )
                  )}
                </select>
              </div>
              <div className="form-group create-property-input-field">
                <label>Main Photo</label>
                <input
                  type="file"
                  name="main_photo"
                  className="form-control"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  onChange={handleMainPhotoChange}
                  disabled={mainPhotoUploading}
                />
                {mainPhotoUploading && (
                  <div style={{ marginTop: "8px" }}>
                    <div
                      style={{
                        width: "100%",
                        backgroundColor: "#e9ecef",
                        borderRadius: "4px",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          width: `${mainPhotoProgress}%`,
                          height: "20px",
                          backgroundColor: "#007bff",
                          transition: "width 0.3s ease",
                        }}
                      ></div>
                    </div>
                    <small style={{ color: "#6c757d" }}>
                      Uploading... {mainPhotoProgress}%
                    </small>
                  </div>
                )}
                {mainPhoto && !mainPhotoUploading && (
                  <div style={{ marginTop: "8px" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        padding: "8px",
                        backgroundColor: "#f8f9fa",
                        borderRadius: "4px",
                        border: "1px solid #dee2e6",
                      }}
                    >
                      <span style={{ fontSize: "14px" }}>
                        ✓ {mainPhoto.name}
                      </span>
                      <button
                        type="button"
                        onClick={removeMainPhoto}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#dc3545",
                          cursor: "pointer",
                          fontSize: "12px",
                          padding: "2px 6px",
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div
                className="form-group create-property-input-field-text"
                style={{ width: "444px", margin: "auto", marginTop: "-12px" }}
              >
                <p>
                  The file can not be larger than 100MB. If you don't want to
                  change file - just leave the field empty.
                </p>
              </div>
              <div className="form-group create-property-input-number">
                <label>
                  Price From <span>*</span>
                </label>
                <input
                  type="number"
                  name="price_from"
                  className={getFieldErrorClass(validationErrors, "price_from")}
                  style={getFieldErrorStyle(
                    validationErrors,
                    "price_from",
                    hasSubmitted
                  )}
                  value={form.price_from}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                />
                {validationErrors.price_from && (
                  <div style={getErrorMessageStyle(hasSubmitted)}>
                    {validationErrors.price_from}
                  </div>
                )}
              </div>
              <div className="form-group create-property-input-number">
                <label>
                  Price to <span>*</span>
                </label>
                <input
                  type="number"
                  name="price_to"
                  className={getFieldErrorClass(validationErrors, "price_to")}
                  style={getFieldErrorStyle(
                    validationErrors,
                    "price_to",
                    hasSubmitted
                  )}
                  value={form.price_to}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                />
                {validationErrors.price_to && (
                  <div style={getErrorMessageStyle(hasSubmitted)}>
                    {validationErrors.price_to}
                  </div>
                )}
              </div>

              <div className="form-group create-property-input-number">
                <label>
                  Actual Market Rent <span>*</span>
                </label>
                <input
                  type="number"
                  name="actual_market_rent"
                  className={getFieldErrorClass(
                    validationErrors,
                    "actual_market_rent"
                  )}
                  style={getFieldErrorStyle(
                    validationErrors,
                    "actual_market_rent",
                    hasSubmitted
                  )}
                  value={form.actual_market_rent}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                />
                {validationErrors.actual_market_rent && (
                  <div style={getErrorMessageStyle(hasSubmitted)}>
                    {validationErrors.actual_market_rent}
                  </div>
                )}
              </div>

              <div className="form-group create-property-input-number">
                <label>Square Footage</label>
                <input
                  type="number"
                  name="square_footage"
                  className="form-control"
                  value={form.square_footage}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group create-property-input-number">
                <label>Rateable Value</label>
                <input
                  type="number"
                  name="rateable_value"
                  className="form-control"
                  value={form.rateable_value}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group create-property-input-number">
                <label>Rebuild Value</label>
                <input
                  type="number"
                  name="rebuild_value"
                  className="form-control"
                  value={form.rebuild_value}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group create-property-input-field-Status">
                <label>
                  Location <span>*</span>{" "}
                </label>
                <select
                  required
                  type="text"
                  name="location_id"
                  className={getFieldErrorClass(
                    validationErrors,
                    "location_id"
                  )}
                  style={getFieldErrorStyle(
                    validationErrors,
                    "location_id",
                    hasSubmitted
                  )}
                  value={form.location_id}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  <option value="" disabled hidden>
                    Select Location
                  </option>
                  {loading ? (
                    <option value="">Loading locations...</option>
                  ) : Object.keys(formData.locations).length === 0 ? (
                    <option value="">No locations available</option>
                  ) : (
                    Object.entries(formData.locations).map(([key, value]) => (
                      <option key={key} value={key}>
                        {value}
                      </option>
                    ))
                  )}
                </select>
                {validationErrors.location_id && (
                  <div style={getErrorMessageStyle(hasSubmitted)}>
                    {validationErrors.location_id}
                  </div>
                )}
              </div>
              <div className="form-group create-property-input-field-Status">
                <label>
                  Type <span>*</span>{" "}
                </label>
                <select
                  required
                  type="text"
                  name="type_id"
                  className={getFieldErrorClass(validationErrors, "type_id")}
                  style={getFieldErrorStyle(
                    validationErrors,
                    "type_id",
                    hasSubmitted
                  )}
                  value={form.type_id}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  <option value="" disabled hidden>
                    Select Type
                  </option>
                  {loading ? (
                    <option value="">Loading types...</option>
                  ) : Object.keys(formData.types).length === 0 ? (
                    <option value="">No types available</option>
                  ) : (
                    Object.entries(formData.types).map(([key, value]) => (
                      <option key={key} value={key}>
                        {value}
                      </option>
                    ))
                  )}
                </select>
                {validationErrors.type_id && (
                  <div style={getErrorMessageStyle(hasSubmitted)}>
                    {validationErrors.type_id}
                  </div>
                )}
              </div>
              <div className="form-group create-property-input-number">
                <label>
                  Spaces (number) <span>*</span>
                </label>
                <input
                  type="number"
                  name="spaces"
                  className={getFieldErrorClass(validationErrors, "spaces")}
                  style={getFieldErrorStyle(
                    validationErrors,
                    "spaces",
                    hasSubmitted
                  )}
                  value={form.spaces}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                />
                {validationErrors.spaces && (
                  <div style={getErrorMessageStyle(hasSubmitted)}>
                    {validationErrors.spaces}
                  </div>
                )}
              </div>
              <div className="form-group create-property-input-number">
                <label>
                  Bathrooms (number) <span>*</span>
                </label>
                <input
                  type="number"
                  name="bathrooms"
                  className={getFieldErrorClass(validationErrors, "bathrooms")}
                  style={getFieldErrorStyle(
                    validationErrors,
                    "bathrooms",
                    hasSubmitted
                  )}
                  value={form.bathrooms}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                />
                {validationErrors.bathrooms && (
                  <div style={getErrorMessageStyle(hasSubmitted)}>
                    {validationErrors.bathrooms}
                  </div>
                )}
              </div>
              <div className="form-group create-property-input-number">
                <label>
                  kitchens (number) <span>*</span>
                </label>
                <input
                  type="number"
                  name="kitchens"
                  className={getFieldErrorClass(validationErrors, "kitchens")}
                  style={getFieldErrorStyle(
                    validationErrors,
                    "kitchens",
                    hasSubmitted
                  )}
                  value={form.kitchens}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                />
                {validationErrors.kitchens && (
                  <div style={getErrorMessageStyle(hasSubmitted)}>
                    {validationErrors.kitchens}
                  </div>
                )}
              </div>
              <div className="form-group create-property-input-field-Status">
                <label>
                  Garden
                </label>
                <select
                  style={{
                    width: "200px",
                  }}
                  type="text"
                  name="garden"
                  className="form-control"
                  value={form.garden}
                  onChange={handleChange}
                >
                  <option value="" disabled hidden>
                    Select a garden
                  </option>
                  <option value="0">No</option>
                  <option value="1">Yes</option>
                </select>
              </div>
              <div className="form-group create-property-input-field-Status">
                <label>
                  Parking
                </label>
                <select
                  style={{
                    width: "200px",
                  }}
                  type="text"
                  name="parking"
                  className="form-control"
                  value={form.parking}
                  onChange={handleChange}
                >
                  <option value="" disabled hidden>
                    Select a parking
                  </option>
                  <option value="0">No</option>
                  <option value="1">Yes</option>
                </select>
              </div>
              <div className="form-group create-property-input-field-radio">
                <label>VIP provided rule </label>
                <small
                  style={{
                    display: "block",
                    marginLeft: "32px",
                    color: "#666",
                    fontStyle: "italic",
                  }}
                >
                  Tip: click a selected option again to clear it.
                </small>
                <div
                  style={{ marginLeft: "32px", display: "flex", gap: "32px" }}
                >
                  <div style={{ width: "190px" }}>
                    <input
                      onChange={handleChange}
                      onClick={() => handleVipRuleTypeClick("0")}
                      type="radio"
                      name="vip_rule_type"
                      value="0"
                      checked={form.vip_rule_type === "0"}
                    />
                    <p>£ 100 - 1st month after 16th</p>
                    <p>£ 200 - 2nd-6th months</p>
                    <p>£ 250 - Onwards</p>
                  </div>
                  <div style={{ width: "190px" }}>
                    <input
                      onChange={handleChange}
                      onClick={() => handleVipRuleTypeClick("1")}
                      name="vip_rule_type"
                      type="radio"
                      value="1"
                      checked={form.vip_rule_type === "1"}
                    />
                    <p>£ 110 - 1st month after 16th</p>
                    <p>£ 220 - 2nd-6th months</p>
                    <p>£ 275 - Onwards</p>
                  </div>
                  <div style={{ width: "190px" }}>
                    <input
                      style={{ marginLeft: "32px" }}
                      onChange={handleChange}
                      onClick={() => handleVipRuleTypeClick("2")}
                      name="vip_rule_type"
                      type="radio"
                      value="2"
                      checked={form.vip_rule_type === "2" || form.vip_rule_type === 2}
                    />
                    <div style={{ textAlign: "center" }}>
                      <p>Unique vip rule</p>
                      <button
                        type="button"
                        onClick={handleAddVipRule}
                        style={{
                          border: "none",
                          background: "transparent",
                          color: "green",
                          fontWeight: "bold",
                          cursor: "pointer",
                          padding: 0,
                          textDecoration: "underline"
                        }}
                      >
                        Add rule
                      </button>
                    </div>
                  </div>
                </div>
                {/* Dynamic VIP Rules Builder */}
                {(form.vip_rule_type === "2" || form.vip_rule_type === 2) && (
                  <div style={{ marginTop: "20px", marginLeft: "32px" }}>
                    {vipRules.length === 0 && (
                      <p style={{ color: "#666", fontStyle: "italic", marginBottom: "10px" }}>
                        Click "Add rule" above to create custom VIP rules
                      </p>
                    )}
                    {vipRules.map((rule, index) => (
                      <div
                        key={index}
                        style={{
                          display: "flex",
                          gap: "10px",
                          marginBottom: "10px",
                          alignItems: "flex-end"
                        }}
                      >
                        <div style={{ width: "200px" }}>
                          <label style={{ display: "block", marginBottom: "5px" }}>
                            For (months)
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={rule.months || ""}
                            onChange={(e) =>
                              handleVipRuleChange(index, "months", e.target.value)
                            }
                            placeholder="Onwards"
                          />
                        </div>
                        <div style={{ width: "200px" }}>
                          <label style={{ display: "block", marginBottom: "5px" }}>
                            Amount
                          </label>
                          <input
                            type="text"
                            value={rule.amount || ""}
                            onChange={(e) =>
                              handleVipRuleChange(index, "amount", e.target.value)
                            }
                            placeholder="Enter amount"
                            className={`form-control${
                              validationErrors.vip_amount_rule &&
                              (!rule.amount || rule.amount.trim() === "")
                                ? " is-invalid"
                                : ""
                            }`}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={handleAddVipRule}
                          style={{
                            border: "none",
                            background: "transparent",
                            color: "green",
                            fontWeight: "bold",
                            cursor: "pointer",
                            height: "34px"
                          }}
                        >
                          Add
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveVipRule(index)}
                          style={{
                            border: "none",
                            background: "transparent",
                            color: "red",
                            fontWeight: "bold",
                            cursor: "pointer",
                            height: "34px"
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    ))}
                    {validationErrors.vip_amount_rule && (
                      <div
                        className="invalid-feedback"
                        style={{ display: "block", marginTop: "5px" }}
                      >
                        {validationErrors.vip_amount_rule}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="form-group create-property-input-field">
                <label>
                  Total VIP Paid
                </label>
                <input
                  placeholder="Enter total VIP paid"
                  type="number"
                  name="total_vip_paid"
                  className="form-control"
                  value={form.total_vip_paid}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group create-property-input-field">
                <label>Date Taken On</label>
                <DatePicker
                  selected={parseDate(form.date_taken_on)}
                  onChange={(date) => setForm({ ...form, date_taken_on: formatDateForBackend(date) })}
                  dateFormat="dd/MM/yyyy"
                  className={`form-control${validationErrors.date_taken_on ? " is-invalid" : ""}`}
                  placeholderText="dd/mm/yyyy"
                  isClearable
                  showYearDropdown
                  scrollableYearDropdown
                  yearDropdownItemNumber={50}
                />
                {validationErrors.date_taken_on && (
                  <div style={{
                    color: "#dc3545",
                    fontSize: "0.875rem",
                    marginTop: "0.25rem",
                    display: "block",
                    fontWeight: "bold",
                    backgroundColor: "#f8d7da",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    border: "1px solid #f5c6cb",
                  }}>
                    {validationErrors.date_taken_on}
                  </div>
                )}
              </div>
              <div className="form-group create-property-input-field">
                <label>Date Live</label>
                <DatePicker
                  selected={parseDate(form.date_live)}
                  onChange={(date) => setForm({ ...form, date_live: formatDateForBackend(date) })}
                  dateFormat="dd/MM/yyyy"
                  className={`form-control${validationErrors.date_live ? " is-invalid" : ""}`}
                  placeholderText="dd/mm/yyyy"
                  isClearable
                  showYearDropdown
                  scrollableYearDropdown
                  yearDropdownItemNumber={50}
                />
                {validationErrors.date_live && (
                  <div style={{
                    color: "#dc3545",
                    fontSize: "0.875rem",
                    marginTop: "0.25rem",
                    display: "block",
                    fontWeight: "bold",
                    backgroundColor: "#f8d7da",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    border: "1px solid #f5c6cb",
                  }}>
                    {validationErrors.date_live}
                  </div>
                )}
              </div>
              <div className="form-group create-property-input-field">
                <label>Date Taken Back</label>
                <DatePicker
                  selected={parseDate(form.date_taken_back)}
                  onChange={(date) => setForm({ ...form, date_taken_back: formatDateForBackend(date) })}
                  dateFormat="dd/MM/yyyy"
                  className={`form-control${validationErrors.date_taken_back ? " is-invalid" : ""}`}
                  placeholderText="dd/mm/yyyy"
                  isClearable
                  showYearDropdown
                  scrollableYearDropdown
                  yearDropdownItemNumber={50}
                />
                {validationErrors.date_taken_back && (
                  <div style={{
                    color: "#dc3545",
                    fontSize: "0.875rem",
                    marginTop: "0.25rem",
                    display: "block",
                    fontWeight: "bold",
                    backgroundColor: "#f8d7da",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    border: "1px solid #f5c6cb",
                  }}>
                    {validationErrors.date_taken_back}
                  </div>
                )}
              </div>
              <div className="form-group create-property-input-field">
                <label>Today</label>
                <DatePicker
                  selected={new Date()}
                  dateFormat="dd/MM/yyyy"
                  className="form-control"
                  readOnly
                  disabled
                />
              </div>
              <div className="form-group create-property-input-field">
                <label>Date Feature From</label>
                <DatePicker
                  selected={parseDate(form.date_feature_from)}
                  onChange={(date) => setForm({ ...form, date_feature_from: formatDateForBackend(date) })}
                  dateFormat="dd/MM/yyyy"
                  className="form-control"
                  placeholderText="dd/mm/yyyy"
                  isClearable
                  showYearDropdown
                  scrollableYearDropdown
                  yearDropdownItemNumber={50}
                />
              </div>
              <div className="form-group create-property-input-field">
                <label>Date Feature To</label>
                <DatePicker
                  selected={parseDate(form.date_feature_to)}
                  onChange={(date) => setForm({ ...form, date_feature_to: formatDateForBackend(date) })}
                  dateFormat="dd/MM/yyyy"
                  className="form-control"
                  placeholderText="dd/mm/yyyy"
                  isClearable
                  showYearDropdown
                  scrollableYearDropdown
                  yearDropdownItemNumber={50}
                />
              </div>
              <div className="form-group create-property-input-field">
                <label>Video</label>
                <input
                  type="text"
                  name="video"
                  className={`form-control${validationErrors.video ? " is-invalid" : ""}`}
                  value={form.video}
                  onChange={handleChange}
                />
                {validationErrors.video && (
                  <div className="invalid-feedback">{validationErrors.video}</div>
                )}
              </div>
              <div className="form-group create-property-input-field">
                <label>3D Mapping</label>
                <input
                  type="text"
                  name="mapping"
                  className="form-control"
                  value={form.mapping}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group create-property-input-field">
                <label>Address 1</label>
                <input
                  type="text"
                  name="address_1"
                  className="form-control"
                  value={form.address_1}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group create-property-input-field">
                <label>Address 2</label>
                <input
                  type="text"
                  name="address_2"
                  className="form-control"
                  value={form.address_2}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group create-property-input-field">
                <label>Address 3</label>
                <input
                  type="text"
                  name="address_3"
                  className="form-control"
                  value={form.address_3}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group create-property-input-field">
                <label>Town</label>
                <input
                  type="text"
                  name="town"
                  className="form-control"
                  value={form.town}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group create-property-input-field">
                <label>Postcode</label>
                <input
                  type="text"
                  name="postcode"
                  className="form-control"
                  value={form.postcode}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  onClick={handleGenerateGeoCodes}
                  style={{
                    marginTop: "8px",
                    marginLeft: "290px",
                    padding: "8px 16px",
                    backgroundColor: "#17264b",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "16px",
                    fontFamily: "Arial, sans-serif",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#36bba5";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "#17264b";
                  }}
                  disabled={!form.postcode || isGeocoding}
                >
                  {isGeocoding
                    ? "Generating..."
                    : "Generate geo codes from the postcode"}
                </button>
              </div>
              <div className="form-group create-property-input-field">
                <label>Geo Latitude</label>
                <input
                  type="text"
                  name="geo_latitude"
                  className="form-control"
                  value={form.geo_latitude}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group create-property-input-field">
                <label>Geo Longitude</label>
                <input
                  type="text"
                  name="geo_longitude"
                  className="form-control"
                  value={form.geo_longitude}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group create-property-textarea-field">
                <label>Property Notes</label>
                <textarea
                  type="text"
                  name="short_description"
                  className="form-control"
                  value={form.short_description}
                  onChange={handleChange}
                  rows={3}
                />
              </div>
              <div
                className="form-group create-property-input-field-text"
                style={{ width: "444px", margin: "auto", marginTop: "-12px" }}
              >
                <p>Internal description.</p>
              </div>
              <div className="form-group create-property-textarea-field">
                <label>Fit out notes</label>
                <textarea
                  type="text"
                  name="fit_out_notes"
                  className="form-control"
                  value={form.fit_out_notes}
                  onChange={handleChange}
                  rows={3}
                />
              </div>
              <div
                className="form-group create-property-input-field-text"
                style={{
                  width: "444px",
                  margin: "auto",
                  marginTop: "-12px",
                  fontWeight: 500,
                }}
              >
                <p>Fit out notes.</p>
              </div>
              <div className="form-group create-property-textarea-field">
                <label>Utilities notes</label>
                <textarea
                  type="text"
                  name="utilities_notes"
                  className="form-control"
                  value={form.utilities_notes}
                  onChange={handleChange}
                  rows={3}
                />
              </div>
              <div
                className="form-group create-property-input-field-text"
                style={{
                  width: "444px",
                  margin: "auto",
                  marginTop: "-12px",
                  fontWeight: 500,
                }}
              >
                <p>Utilities notes.</p>
              </div>
              <div className="form-group create-property-textarea-field">
                <label>Website Display Name</label>
                <input
                  type="text"
                  name="website_display_name"
                  className="form-control"
                  value={form.website_display_name}
                  onChange={handleChange}
                  placeholder="e.g. Edwards Drive, London — leave blank to use address"
                />
              </div>
              <div
                className="form-group create-property-input-field-text"
                style={{
                  width: "444px",
                  margin: "auto",
                  marginTop: "-12px",
                  fontWeight: 500,
                }}
              >
                <p>Custom name shown on the website. If empty, the address will be used.</p>
              </div>
              <div className="form-group create-property-textarea-field">
                <label>Short Description</label>
                <textarea
                  type="text"
                  name="public_short_description"
                  className="form-control"
                  value={form.public_short_description}
                  onChange={handleChange}
                  rows={3}
                />
              </div>
              <div
                className="form-group create-property-input-field-text"
                style={{
                  width: "444px",
                  margin: "auto",
                  marginTop: "-12px",
                  fontWeight: 500,
                }}
              >
                <p>Public short description.</p>
              </div>
              <div className="form-group create-property-textarea-field">
                <label>Description</label>
                <div style={{ width: "444px", marginLeft: "32px" }}>
                  <CKEditor
                    editor={ClassicEditor}
                    data={form.public_description || ""}
                    config={{
                    licenseKey: "GPL",
                    height: "200px",
                    toolbar: [
                      "heading",
                      "|",
                      "bold",
                      "italic",
                      "link",
                      "bulletedList",
                      "numberedList",
                      "|",
                      "outdent",
                      "indent",
                      "|",
                      "blockQuote",
                      "undo",
                      "redo",
                    ],
                    placeholder: "Enter public description here...",
                    removePlugins: [
                      "CKFinderUploadAdapter",
                      "CKFinder",
                      "EasyImage",
                      "Image",
                      "ImageCaption",
                      "ImageStyle",
                      "ImageToolbar",
                      "ImageUpload",
                    ],
                    }}
                    onChange={(event, editor) => {
                      const data = editor.getData();
                      setForm((prev) => ({ ...prev, public_description: data }));
                    }}
                  />
                </div>
              </div>
              <div
                className="form-group create-property-input-field-text"
                style={{
                  width: "444px",
                  margin: "auto",
                  marginTop: "-12px",
                  fontWeight: 500,
                }}
              >
                <p>Public description visible for guardians.</p>
              </div>
              <div
                className="form-group create-property-input-field"
                style={{ display: "flex", alignItems: "start" }}
              >
                <label>Information</label>
                <div
                  className="form-group create-property-input-field-text"
                  style={{
                    // width: "444px",
                    // margin: "auto",
                    marginLeft: "32px",
                    // paddingTop: "16px",
                    fontWeight: 500,
                  }}
                >
                  <p>
                    After you create a property you may manage rooms and assign
                    guardians.
                  </p>
                </div>
              </div>

              {/* ===================================================================== start here =========== */}
              {/* <div style={{ flex: 1, minWidth: 260 }}>
                <div className="form-group">
                  <label>Inspection Route</label>
                  <input
                    type="text"
                    name="inspection_route_id"
                    className="form-control"
                    value={form.inspection_route_id}
                    onChange={handleChange}
                  />
                </div>
              </div> */}
            </div>
          </div>

          {/* Liabilities Section */}
        </div>
        <div
          className="section mb-4"
          style={{ background: "#fff", padding: "16px", border: "none" }}
        >
          <div className="panel-title" style={{ border: "none" }}>
            <h4>Liabilities</h4>
          </div>
          <div className="panel-body" style={{ border: "none" }}>
            <table className="table " style={{ width: "100%", border: "none" }}>
              <thead style={{ border: "none" }}>
                <tr style={{ border: "none" }}>
                  <th>Name</th>
                  <th>Work</th>
                  <th>Cost</th>
                </tr>
              </thead>
              <tbody style={{ border: "none" }}>
                {formData.liabilityTypes && formData.liabilityTypes.length > 0 ? (
                  formData.liabilityTypes.map((type) => (
                    <tr key={type.id} style={{ border: "none" }}>
                      <td>{type.name}</td>
                      <td>
                        <select
                          className="form-control"
                          name={`liability[${type.id}][work]`}
                          value={form.liability?.[type.id]?.work || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            setForm((prev) => ({
                              ...prev,
                              liability: {
                                ...prev.liability,
                                [type.id]: {
                                  ...prev.liability?.[type.id],
                                  work: value || null,
                                },
                              },
                            }));
                          }}
                          required
                        >
                          <option value="">Please Select</option>
                          {Object.entries(formData.liabilityOptions || {}).map(
                            ([optionId, optionName]) => (
                              <option key={optionId} value={optionId}>
                                {optionName}
                              </option>
                            )
                          )}
                        </select>
                      </td>
                      <td>
                        <select
                          className="form-control"
                          name={`liability[${type.id}][cost]`}
                          value={form.liability?.[type.id]?.cost || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            setForm((prev) => ({
                              ...prev,
                              liability: {
                                ...prev.liability,
                                [type.id]: {
                                  ...prev.liability?.[type.id],
                                  cost: value || null,
                                },
                              },
                            }));
                          }}
                          required
                        >
                          <option value="">Please Select</option>
                          {Object.entries(formData.liabilityOptions || {}).map(
                            ([optionId, optionName]) => (
                              <option key={optionId} value={optionId}>
                                {optionName}
                              </option>
                            )
                          )}
                        </select>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" style={{ textAlign: "center", padding: "20px" }}>
                      {loading ? "Loading liability types..." : "No liability types available"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="panel-footer text-center create-property-footer">
          <button 
            type="submit" 
            className="btn btn-head btn-standard"
            disabled={isSubmitting}
          >
            <i className="fa fa-plus" style={{ marginRight: 6 }} /> 
            {isSubmitting ? "Creating..." : "Add Property"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PropertyCreate;
